/**
 * What a raw source upload actually did, in words staff can act on (GDN-4124).
 *
 * On 2026-09-24 a 120,973-file drop had every browser PUT to S3 fail, and the
 * uploader still read "Uploading 120973/120973 files… 0%". The counter
 * advanced on failure as well as success, the S3 error was reduced to a bare
 * status, and the only error anyone saw was the server's `batch_not_found` on
 * ~1,965 continuation presigns — a symptom of the earlier PUTs never landing,
 * not the cause. This module keeps those facts apart.
 */

/** A direct-to-S3 PUT that did not land, with what S3 (or the browser) said. */
export class S3UploadError extends Error {
  /** The HTTP status, or 0 when the browser got no response at all. */
  readonly status: number;
  /** S3's error code from the XML body (e.g. `AccessDenied`), when it sent one. */
  readonly code?: string;
  /** S3's own sentence from the XML body, when it sent one. */
  readonly s3Message?: string;

  constructor(status: number, code?: string, s3Message?: string) {
    super(
      status === 0
        ? 'Network error: the browser got no response from S3'
        : `S3 refused the upload (HTTP ${status}${code ? ` ${code}` : ''})${s3Message ? `: ${s3Message}` : ''}`,
    );
    this.name = 'S3UploadError';
    this.status = status;
    this.code = code;
    this.s3Message = s3Message;
  }
}

function xmlField(body: string, tag: string): string | undefined {
  const match = new RegExp(`<${tag}>([^<]*)</${tag}>`).exec(body);
  const value = match?.[1]?.trim();
  return value ? value : undefined;
}

/**
 * Build the error for a PUT that got a response. S3 answers a refusal with an
 * XML `<Error><Code/><Message/></Error>` body; anything else keeps the status.
 */
export function s3ErrorFromResponse(
  status: number,
  body: string | null | undefined,
): S3UploadError {
  const text = body ?? '';
  return new S3UploadError(status, xmlField(text, 'Code'), xmlField(text, 'Message'));
}

const ONLINE_ONLY =
  'Files that are online-only in Google Drive, Dropbox or OneDrive cannot be read by the browser. Copy the folder to local disk and upload the copy.';
const PAGE_ADDRESS =
  'S3 accepts uploads only from approved page addresses. If this page was opened from a preview or local address, open it from the usual admin address.';
const LINK_EXPIRED = 'Upload links expire. Start the upload again to get fresh links.';
const S3_TRANSIENT =
  'S3 reported an error on its side. Start the upload again; if it repeats, report it with this message.';

/**
 * Likely causes of a failed PUT, for staff who cannot see the network log,
 * chosen by what S3 (or the browser) said. Nothing for an error that did not
 * come from a PUT: these causes would read as a diagnosis of the wrong thing.
 */
export function uploadFailureGuidance(error: unknown): string[] {
  if (!(error instanceof S3UploadError)) return [];
  // No response: an unreadable (online-only) file and a CORS refusal look
  // identical from the page.
  if (error.status === 0) return [ONLINE_ONLY, PAGE_ADDRESS];
  if (error.status === 403) return [LINK_EXPIRED, PAGE_ADDRESS];
  if (error.status >= 500) return [S3_TRANSIENT];
  return [LINK_EXPIRED];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * The first error, as one line. Never blank. A refused presign arrives as
 * `auth0BaseQuery`'s envelope, not an `Error`, with the server's sentence at
 * `data.message`.
 */
export function describeUploadError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  const envelope = asRecord(asRecord(error)?.data);
  const message = envelope?.message ?? asRecord(envelope?.data)?.error;
  if (typeof message === 'string' && message.trim()) return message.trim();
  return 'Upload failed with an unknown error';
}

/**
 * The server's refusal reason for a presign, from
 * `MigrationErrorResponse.details.reason`. The body sits inside
 * `auth0BaseQuery`'s envelope (`error.data.data`), or directly at
 * `error.data` without one.
 */
export function readPresignBlockReason(error: unknown): string | undefined {
  const outer = asRecord(asRecord(error)?.data);
  for (const body of [asRecord(outer?.data), outer]) {
    const reason = asRecord(body?.details)?.reason;
    if (typeof reason === 'string') return reason;
  }
  return undefined;
}

/**
 * A presign refusal no retry against this batch can cure. Everything the
 * server blocks with is permanent except a conversion-status lookup that
 * failed, which may answer next time.
 */
export function isPermanentPresignRefusal(reason: string | undefined): reason is string {
  return reason !== undefined && reason !== 'conversion_status_unavailable';
}

/**
 * A refusal about the whole batch (it is gone, converted or unknown), as
 * opposed to one about a single file's upload. Only these end a run.
 */
export function isBatchRefusal(reason: string | undefined): boolean {
  return (
    reason === 'batch_not_found' ||
    reason === 'batch_deleted' ||
    reason === 'batch_already_converted' ||
    reason === 'batch_conversion_started' ||
    reason === 'invalid_upload_id' ||
    reason === 'continuation_requires_upload_id'
  );
}

/** A count with thousands separators: 120,973 reads, 120973 does not. */
const n = (value: number): string => value.toLocaleString('en-US');

export interface UploadCounts {
  total: number;
  uploaded: number;
  failed: number;
  skipped: number;
}

/**
 * The live progress line. Uploaded and failed are counted separately, so it
 * can never read "N/N" while nothing has landed.
 */
export function uploadProgressText(counts: UploadCounts, percent: number): string {
  const parts = [`${n(counts.uploaded)} of ${n(counts.total)} uploaded`];
  if (counts.failed > 0) parts.push(`${n(counts.failed)} failed`);
  if (counts.skipped > 0) parts.push(`${n(counts.skipped)} duplicate(s) skipped`);
  return `Uploading: ${parts.join(', ')}… ${percent}%`;
}

export type UploadStop =
  /** A presigned batch had targets and nothing in the run has landed. */
  | { kind: 'landed_nothing' }
  /** The server refused a presign in a way no retry cures. */
  | { kind: 'refused'; reason: string }
  /** The first presign of a new upload failed, so nothing was sent. */
  | { kind: 'not_started' }
  /** Staff cancelled the run. */
  | { kind: 'cancelled' };

export interface UploadOutcome extends UploadCounts {
  /** Why the run stopped early, if it did. */
  stopped?: UploadStop;
  /** Whether any PUT to S3 was attempted in the run. */
  putsAttempted: boolean;
  /** Files never attempted: the run stopped, or the server returned no link. */
  notAttempted: number;
  /** The error of the earliest file still failing, else the refusal, as one line. */
  firstError?: string;
  /** Likely causes for that error; empty when it did not come from a PUT. */
  guidance: string[];
}

/** The headline for a finished run that had failures or stopped. */
export function uploadOutcomeHeadline(outcome: UploadOutcome): string {
  const base = `${n(outcome.uploaded)} of ${n(outcome.total)} uploaded, ${n(outcome.failed)} failed`;
  const skipped = outcome.skipped > 0 ? `, ${n(outcome.skipped)} duplicate(s) skipped` : '';
  const notAttempted =
    outcome.notAttempted > 0
      ? `${
          outcome.stopped?.kind === 'cancelled'
            ? '. Cancelled: '
            : outcome.stopped
              ? '. Stopped early: '
              : ', '
        }${n(outcome.notAttempted)} not attempted`
      : '';
  // A cancel that came too late to stop anything still explains the warning.
  const cancelledLate =
    outcome.stopped?.kind === 'cancelled' && outcome.notAttempted === 0 ? '. Cancelled' : '';
  return `${base}${skipped}${notAttempted}${cancelledLate}.`;
}

/**
 * Why the run stopped, in a sentence, or nothing when stopping cost nothing
 * (a batch that landed nothing was also the last one).
 */
export function uploadStopExplanation(outcome: UploadOutcome): string | undefined {
  const stop = outcome.stopped;
  if (!stop) return undefined;
  if (stop.kind === 'cancelled') {
    return 'The upload was cancelled. Files that finished before it took effect stay in this upload; a large file still in progress was discarded.';
  }
  if (stop.kind === 'not_started') {
    return 'The upload could not start, so no files were sent. Try again; if it repeats, report it with this message.';
  }
  if (stop.kind === 'landed_nothing') {
    return outcome.notAttempted > 0
      ? 'Not one file in a group reached S3, so the rest were not attempted. They would fail the same way.'
      : undefined;
  }
  switch (stop.reason) {
    case 'batch_not_found':
      // The server finds the batch once one object lands, so this means
      // nothing has: either the PUTs above failed, or the upload being added
      // to never had files.
      return outcome.putsAttempted
        ? 'The server found no files for this upload in S3, so the uploads before it did not land. The first error explains why.'
        : 'The upload these files were being added to has no files in S3, so the server would not add to it. Start a new upload instead.';
    case 'batch_deleted':
      return 'This upload was deleted while files were being added to it. Start a new upload.';
    case 'batch_already_converted':
    case 'batch_conversion_started':
      return 'Conversion of this upload has started, so no more files can be added to it. Upload the remaining files as a new upload.';
    default:
      return `The server refused to add files to this upload (${stop.reason}). Report it with this message.`;
  }
}
